import { inject, injectable } from 'inversify';

import AlertDialog from '../components/ui/dialogs/alert-dialog.component';
import { Logger } from '../models/logger';
import { hasHumanName } from '../utils/upload-failure';
import { ToastService } from './toast.service';
import type { ToastHandle } from './toast.service';

declare var window;
declare var Swal;

export type Millisecond = number;

export interface ShowErrorDialogRequest {
    title: string;
    html?: string;
    text?: string;
    confirmButtonText?: string;
    successCallback?: () => void;
    showCancelButton?: boolean;
    cancelButtonText?: string;
    cancelCallback?: () => void;
    /** Called once when the dialog goes away, by any route (a button, Escape, `closeRef`'s function). */
    onClosed?: () => void;
    /** Hands the caller a function that closes the dialog (fu-r2: the offline dialog closes itself on `online`). */
    closeRef?: (close: () => void) => void;
    /** UXFU2-03: where focus goes when the dialog closes and nothing opened it (an upload failure from the background). */
    returnFocusTo?: () => HTMLElement | null | undefined;
}

@injectable()
export class UIContext {
    private readonly logger = Logger.getLogger('UIContext');

    constructor(
        @inject(ToastService) private toastr: ToastService,
    ) { }

    successToast(title?: string, message?: string, timeout: Millisecond = 5000) {
        this.toastr.success(message, title, {
            timeout: timeout,
        });
    }

    errorToast(title: string, message: string, isImportant?: boolean, shouldErrorForSentry?: boolean) {
        // this.logToSentry(title, message, isImportant, shouldErrorForSentry);
        this.logger.error({message: message });
        this.toastr.error(message, title);
    }

    warnToast(title: string, message: string, isImportant: boolean, shouldErrorForSentry: boolean) {
        // this.logToSentry(title, message, isImportant, shouldErrorForSentry);
        this.logger.warn({message: message });
        this.toastr.warning(message, title);
    }

    /**
     * F3r (review 7): a NON-BLOCKING notice with one action -- a warning toast that stays until closed or acted on,
     * announced (`role="status"`), never a dialog over the page. For a problem that must not stop what else the page
     * does (the sign-in give-up: finished downloads need no identity).
     */
    noticeWithAction(title: string, message: string, actionLabel: string, onAction: (ref: ToastHandle) => void) {
        this.logger.warn({ message });
        this.toastr.warning(title, message, { role: 'status', mustClose: true, actionLabel, onAction });
    }

    handleError(err: Error, options?: {
        confirmButtonText?: string,
        confirmButtonCallback?: () => void
    }) {
        this.logger.error(err);
        // UXFU1-02: never title a modal with a class name ("ClientError", "TimeoutError", "Error"). Errors named for
        // people ("File is empty") keep their name; everything else gets a human title.
        const errorTitle = hasHumanName(err) ? err.name : 'Something went wrong';
        const errorMessage = err && err.message || 'Please try again.';
        AlertDialog.create({
            title: errorTitle,
            text: errorMessage,
            type: 'danger',
            showCancelButton: false,
            confirmButtonText: options?.confirmButtonText || 'Ok!',
            confirmButtonCallback: options?.confirmButtonCallback
        });
    }

    private logToSentry(title: string, data: any, isImportant = true, shouldErrorForSentry = true) {
        
    }

    private stackTrace() {
        const err = new Error();
        return err.stack;
    }

    showErrorDialog(request: ShowErrorDialogRequest) {
       const logText = request.text || (request.html ? request.html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : 'No details');
       try {
        this.logger.error({ message: `${request.title} ${logText}`, data: request});
       }catch(e) {
        // prevent circular ref errors
        this.logger.error({ message: `${request.title} ${logText}`});
       }
        AlertDialog.create({
            title: request.title,
            text: request.text,
            html: request.html,
            type: 'danger',
            showCancelButton: request.showCancelButton || false,
            confirmButtonText: request.confirmButtonText || 'Ok!',
            cancelButtonText: request.cancelButtonText || 'Cancel',
            confirmButtonCallback: request.successCallback,
            cancelButtonCallback: request.cancelCallback,
            onClosed: request.onClosed,
            closeRef: request.closeRef,
            returnFocusTo: request.returnFocusTo,
        });
    }

    showSuccessDialog(title: string, text: string, html?: string) {
        AlertDialog.create({
            title: title,
            text: text,
            html: html,
            type: 'success',
            showCancelButton: false,
            confirmButtonText: 'Ok!',
        });
    }

    showConfirmationWarningDialog(
        title: string,
        message: string,
        onConfirmationFn: () => void,
        showCancelButton = true,
        cancelButtonText = 'No',
        confirmButtonText = 'Yes',
        cancelButtonCallback = () => null,
    ) {
        AlertDialog.create({
            title: title,
            text: message,
            type: 'warning',
            showCancelButton: showCancelButton,
            cancelButtonText: cancelButtonText,
            confirmButtonText: confirmButtonText,
            confirmButtonCallback: onConfirmationFn,
            cancelButtonCallback: cancelButtonCallback,
        });
    }

    showDialogWithInput(title: string, text: string, placeholder: string): Promise<string> {
        return Promise.reject('Not implemented!')
        // return new Promise((resolve, reject) => {
        //     Swal({
        //         title: title,
        //         text: text,
        //         input: 'email',
        //         inputPlaceholder: placeholder,
        //         showCancelButton: true,
        //         inputValidator: (value) => {
        //             return !value && 'Please enter your e-mail!';
        //         },
        //     })
        //         .then(data => {
        //             if (data.dismiss) {
        //                 reject(data.dismiss);
        //             } else if (data.value) {
        //                 resolve(data.value);
        //             }
        //             reject(data);
        //         })
        //         .catch(err => reject(err));
        // });
    }
}
